import { useEffect, useState, useRef } from "react";
import api, { apiError, API } from "@/lib/api";
import { SIDEBAR_ASSOCIATIONS, EVENT_CATEGORIES, money, fmtDate } from "@/lib/ui";
import { NAV } from "@/components/Layout";
import CountRulesEditor from "@/components/CountRulesEditor";
import PrcaDataManager from "@/components/PrcaDataManager";
import { Settings as SettingsIcon, Plus, Trash2, X, Loader2, Upload, Download, Globe, Award, Trophy, MessageSquareWarning, ThumbsUp, ThumbsDown, CheckCircle2, Send, Copy, UserPlus, Eye, EyeOff, ToggleLeft } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({
  name: "", associations: ["CPRA"], city: "", state: "AB", lat: 51, lng: -114,
  added_money: 15000, entry_open: "", entry_close: "", ground_rules: "",
  events: ["Bull Riding", "Barrel Racing", "Team Roping"],
  performances: [{ label: "Perf #1", type: "perf", time: "7:00 PM", date: "" }],
});

export default function Admin() {
  const [rodeos, setRodeos] = useState([]);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [drawsLoading, setDrawsLoading] = useState(false);
  const [pruneLoading, setPruneLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [cfrLoading, setCfrLoading] = useState(false);
  const [drawStatus, setDrawStatus] = useState(null);
  const [assocStatus, setAssocStatus] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/rodeos").then((r) => setRodeos(r.data));
  const loadDrawStatus = () => api.get("/rodeos/draws/status").then((r) => setDrawStatus(r.data)).catch(() => {});
  const loadAssocStatus = () => api.get("/admin/import/status").then((r) => setAssocStatus(r.data)).catch(() => {});
  const loadScrapeStatus = () => api.get("/admin/scrapes/status").then((r) => setScrapeStatus(r.data)).catch(() => {});
  const loadFeedback = () => api.get("/admin/coach-feedback").then((r) => setFeedback(r.data)).catch(() => {});
  useEffect(() => { load(); loadDrawStatus(); loadAssocStatus(); loadScrapeStatus(); loadFeedback(); }, []);
  useEffect(() => {
    const running = assocStatus && (assocStatus.cca?.running || assocStatus.prca?.running);
    if (!running) { loadScrapeStatus(); return; }
    const id = setInterval(loadAssocStatus, 4000);
    return () => clearInterval(id);
  }, [assocStatus]);

  const importAssoc = async (which) => {
    try {
      const { data } = await api.post(`/admin/import/${which}`);
      if (data.started) toast.success(`${which.toUpperCase()} import started — pulling rodeos${which === "cca" ? " & results" : ""}`);
      else toast.message(data.message || "Already running");
      setTimeout(loadAssocStatus, 800);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [navFlags, setNavFlags] = useState({});
  const [savingFlags, setSavingFlags] = useState(false);
  const loadNavFlags = () => api.get("/nav-flags").then((r) => setNavFlags(r.data.flags || {})).catch(() => {});
  useEffect(() => { loadNavFlags(); }, []);
  const isReleased = (to) => navFlags[to] !== false;
  const toggleFlag = (to) => setNavFlags((p) => ({ ...p, [to]: p[to] === false ? true : false }));
  const saveFlags = async () => {
    setSavingFlags(true);
    try {
      await api.put("/admin/nav-flags", { flags: navFlags });
      toast.success("Menu rollout saved — changes are live for users");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingFlags(false); }
  };

  const resolveFeedback = async (id) => {
    try { await api.post(`/admin/coach-feedback/${id}/resolve`); loadFeedback(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const requestReview = async (id) => {
    try { await api.post(`/admin/coach-feedback/${id}/request-review`); toast.success("Sent to the reviewers' queue"); loadFeedback(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const [reviewers, setReviewers] = useState([]);
  const [newReviewer, setNewReviewer] = useState("");
  const loadReviewers = () => api.get("/admin/reviewers").then((r) => setReviewers(r.data.reviewers)).catch(() => {});
  useEffect(() => { loadReviewers(); }, []);
  const addReviewer = async () => {
    if (!newReviewer.trim()) return;
    try { await api.post("/admin/reviewers", { email: newReviewer.trim() }); setNewReviewer(""); loadReviewers(); toast.success("Reviewer added"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const removeReviewer = async (id) => {
    try { await api.delete(`/admin/reviewers/${id}`); loadReviewers(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const copyForAI = (f) => {
    const lines = [
      "COACH FEEDBACK TO FIX",
      `Athlete note: ${f.note}`,
      f.question ? `Question asked: ${f.question}` : "",
      f.answer ? `Coach's flagged answer: ${f.answer}` : "",
      "",
      "EXPERT ANSWERS (source of truth):",
    ];
    (f.expert_responses || []).forEach((r) => {
      lines.push(`- ${r.reviewer_name}: ${r.answer}`);
      (r.links || []).forEach((l) => lines.push(`  link: ${l}`));
      (r.files || []).forEach((fl) => lines.push(`  doc: ${fl.filename}`));
    });
    navigator.clipboard?.writeText(lines.filter(Boolean).join("\n")).then(
      () => toast.success("Copied — paste this to the AI agent"),
      () => toast.error("Couldn't copy"));
  };

  const scrapeImport = async () => {
    setScraping(true);
    try {
      const { data } = await api.post("/rodeos/import/scrape");
      toast.success(`Imported ${data.created} new, refreshed ${data.updated} existing`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setScraping(false); }
  };

  const importStandings = async () => {
    setStandingsLoading(true);
    try {
      const { data } = await api.post("/standings/import");
      toast.success(`Imported ${data.imported} standings across ${data.events} events`);
      loadScrapeStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setStandingsLoading(false); }
  };

  const importAllDraws = async () => {
    setDrawsLoading(true);
    try {
      const { data } = await api.post("/rodeos/import/draws-all");
      toast.success(`Draws: ${data.with_draws} posted, ${data.no_draws} pending · ${data.created} added, ${data.updated} updated`);
      load();
      loadDrawStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDrawsLoading(false); }
  };

  const pruneNoData = async () => {
    if (!window.confirm("Remove all rodeos that don't have real draw data yet? (Keeps only Canadian CPRA rodeos we can source.)")) return;
    setPruneLoading(true);
    try {
      const { data } = await api.post("/rodeos/prune-no-data");
      toast.success(`Removed ${data.deleted} rodeos · ${data.remaining} real rodeos remaining`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPruneLoading(false); }
  };

  const importResults = async () => {
    setResultsLoading(true);
    try {
      const { data } = await api.post("/results/import");
      toast.success(`Results: ${data.pages_imported} rodeos, ${data.pages_matched_to_rodeo} matched · ${data.result_rows} payout rows`);
      loadScrapeStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setResultsLoading(false); }
  };

  const importCfr = async () => {
    setCfrLoading(true);
    try {
      const { data } = await api.post("/cfr/results/import");
      if (data.ok) toast.success(`CFR refreshed: ${data.rounds_saved} round(s), ${data.competitors} riders`);
      else toast.error(data.error || "CFR refresh failed");
      loadScrapeStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCfrLoading(false); }
  };

  const input = "w-full bg-white border border-[#D7DBE0] rounded-lg px-3 py-2.5 text-sm focus:border-[#C8000F] focus:outline-none";
  const label = "text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block";
  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const RefreshLine = ({ st, testid, label }) => {
    if (!st?.last_run) return null;
    return (
      <p className="text-xs text-slate-500 mt-2" data-testid={testid}>
        {label ? <b className="uppercase mr-1">{label}</b> : null}
        Last {st.trigger === "auto" ? "auto-refresh" : "refresh"}: {new Date(st.last_run).toLocaleString()}
        {st.count_text ? ` · ${st.count_text}` : ""}
      </p>
    );
  };
  const setPerf = (i, k, v) => setForm((f) => { const p = [...f.performances]; p[i] = { ...p[i], [k]: v }; return { ...f, performances: p }; });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/rodeos", { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), added_money: parseInt(form.added_money) });
      toast.success("Rodeo added"); setForm(blank()); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (id) => { try { await api.delete(`/rodeos/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete"); } };

  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/rodeos/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} rodeos${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
      if (data.errors.length) console.warn(data.errors);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("rp_token");
    const res = await fetch(`${API}/rodeos/import/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "rodeo_import_template.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="px-4 sm:px-6 py-6">
      <div className="flex items-center gap-2 mb-6"><SettingsIcon className="w-6 h-6 text-[#C8000F]" /><h1 className="font-display text-2xl font-extrabold tracking-tight">Settings</h1></div>

      {/* AI Coach feedback — thumbs down + notes to improve the coach */}
      <div className="rounded-xl border-2 border-[#E68A2E]/40 bg-[#FDF6EC] p-5 mb-6" data-testid="coach-feedback-admin">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-bold flex items-center gap-2"><MessageSquareWarning className="w-4 h-4 text-[#E68A2E]" /> AI Strategy Coach — Feedback</div>
          {feedback && (
            <div className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 text-[#3B6B1E] font-semibold"><ThumbsUp className="w-3.5 h-3.5" /> {feedback.up_count}</span>
              <span className="flex items-center gap-1 text-[#C8000F] font-semibold"><ThumbsDown className="w-3.5 h-3.5" /> {feedback.down_count}</span>
            </div>
          )}
        </div>
        <p className="text-sm text-slate-600 mb-4">Every thumbs-down on the coach shows up here with the athlete's note, the question they asked, and the answer they got — so you can tune the coach. {feedback ? <b>{feedback.open_count} open</b> : null}</p>
        {!feedback ? (
          <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#E68A2E]" /></div>
        ) : feedback.items.length === 0 ? (
          <div className="text-sm text-slate-500">No thumbs-down yet. 🎉</div>
        ) : (
          <div className="space-y-3 max-h-[520px] overflow-y-auto">
            {feedback.items.map((f) => (
              <div key={f.id} className={`rounded-lg border p-3 ${f.resolved ? "border-[#E4E7EB] bg-white/60 opacity-70" : "border-[#E68A2E]/40 bg-white"}`} data-testid={`feedback-item-${f.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] text-slate-400 mb-1">{f.competitor_name || f.email || "Athlete"} · {f.created_at ? new Date(f.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</div>
                    <div className="text-sm font-semibold text-[#9E0008] mb-1.5">“{f.note}”</div>
                    {f.question && <div className="text-xs text-slate-500 mb-1"><b>Asked:</b> {f.question}</div>}
                    {f.answer && <div className="text-xs text-slate-500 line-clamp-4"><b>Coach said:</b> {f.answer}</div>}
                  </div>
                  {f.resolved ? (
                    <span className="shrink-0 text-[11px] text-[#3B6B1E] font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Resolved</span>
                  ) : (
                    <button onClick={() => resolveFeedback(f.id)} data-testid={`resolve-feedback-${f.id}`} className="shrink-0 text-xs font-semibold text-[#3B6B1E] border border-[#3B6B1E]/40 rounded-lg px-2.5 py-1.5 hover:bg-[#3B6B1E]/10">Mark resolved</button>
                  )}
                </div>

                {/* Expert answers from Ryan & Steele */}
                {(f.expert_responses || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {f.expert_responses.map((r, i) => (
                      <div key={i} className="rounded-lg border-2 border-[#3B6B1E]/30 bg-[#F3F8EF] p-3" data-testid={`admin-expert-response-${f.id}-${i}`}>
                        <div className="text-[11px] font-bold text-[#3B6B1E] mb-1">✔ Expert answer — {r.reviewer_name}</div>
                        <div className="text-sm text-[#14181F] whitespace-pre-wrap">{r.answer}</div>
                        {(r.links || []).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {r.links.map((l, li) => (
                              <a key={li} href={l} target="_blank" rel="noreferrer" className="block text-xs text-[#1D4ED8] hover:underline break-all">🔗 {l}</a>
                            ))}
                          </div>
                        )}
                        {(r.files || []).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {r.files.map((fl, fi) => (
                              <a key={fi} href={`${API}/files/${fl.path}?auth=${localStorage.getItem("rp_token")}`} target="_blank" rel="noreferrer" className="block text-xs text-[#1D4ED8] hover:underline break-all">📄 {fl.filename}</a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Send-to-reviewers control */}
                {!f.resolved && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {f.in_review ? (
                      <span className="text-[11px] font-semibold text-[#E68A2E] flex items-center gap-1"><Send className="w-3 h-3" /> {(f.expert_responses || []).length > 0 ? "Answered — in review queue" : "Sent to reviewers — awaiting answer"}</span>
                    ) : (
                      <button onClick={() => requestReview(f.id)} data-testid={`send-review-${f.id}`} className="text-xs font-semibold text-white bg-[#E68A2E] hover:bg-[#cf7a24] rounded-lg px-3 py-1.5 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Send to reviewers</button>
                    )}
                    {(f.expert_responses || []).length > 0 && (
                      <button onClick={() => copyForAI(f)} data-testid={`copy-ai-${f.id}`} className="text-xs font-semibold text-[#1D4ED8] border border-[#1D4ED8]/40 rounded-lg px-3 py-1.5 hover:bg-[#1D4ED8]/10 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy for AI</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coach reviewers — promote sign-ups to reviewer (they keep their own password) */}
      <div className="rounded-xl border border-[#E4E7EB] p-5 mb-6" data-testid="reviewers-admin">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#3B6B1E]" /> Coach Reviewers</div>
        <p className="text-sm text-slate-500 mb-3">These people can open the Review Queue and answer flagged coach replies. They sign up themselves (their own password), then you add them here by email.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={newReviewer} onChange={(e) => setNewReviewer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addReviewer()}
            placeholder="reviewer@email.com (must have signed up)" data-testid="add-reviewer-input"
            className="flex-1 min-w-[220px] rounded-lg border border-[#D7DBE0] px-3 py-2 text-sm focus:outline-none focus:border-[#3B6B1E]" />
          <button onClick={addReviewer} data-testid="add-reviewer-btn" className="flex items-center gap-1.5 bg-[#3B6B1E] hover:bg-[#2f5518] text-white rounded-lg px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add reviewer</button>
        </div>
        {reviewers.length === 0 ? (
          <div className="text-sm text-slate-400">No reviewers yet. Have Ryan &amp; Steele sign up, then add their emails above.</div>
        ) : (
          <div className="space-y-1.5">
            {reviewers.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg bg-[#FAFBFC] border border-[#EEF0F2] px-3 py-2" data-testid={`reviewer-${r.id}`}>
                <span className="text-sm"><b>{r.name || "Reviewer"}</b> · <span className="text-slate-500">{r.email}</span></span>
                <button onClick={() => removeReviewer(r.id)} data-testid={`remove-reviewer-${r.id}`} className="text-slate-400 hover:text-[#C8000F]"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Data cleanup — keep only real-data (Canadian CPRA) rodeos */}
      <div className="rounded-xl border-2 border-[#C8000F]/30 bg-[#FBF4F5] p-5 mb-6">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><Trash2 className="w-4 h-4 text-[#C8000F]" /> Clean Up Rodeos</div>
        <p className="text-sm text-slate-600 mb-4">Remove every rodeo we don't yet have real draw data for. Keeps only Canadian CPRA rodeos sourced from rodeocanada.com. We'll add more associations as we find sources with real draws.</p>
        <button onClick={pruneNoData} disabled={pruneLoading} data-testid="prune-no-data-btn" className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
          {pruneLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove Rodeos Without Draw Data
        </button>
      </div>

      {/* CPRA draws bulk import */}
      <div className="rounded-xl border-2 border-[#C8000F]/30 bg-[#FBF4F5] p-5 mb-6">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><Download className="w-4 h-4 text-[#C8000F]" /> CPRA Draws (rodeocanada.com)</div>
        <p className="text-sm text-slate-600 mb-4">Pull the real draw sheet for every CPRA rodeo from the official 2026 schedule in one click. Each rodeo gets its own correct draw; rodeos whose draws aren't posted yet stay marked "Draw not posted yet". Auto-refreshes daily. Safe to re-run any time.</p>
        <button onClick={importAllDraws} disabled={drawsLoading} data-testid="import-all-draws-btn" className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
          {drawsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import All CPRA Draws
        </button>
        {drawStatus?.last_run && (
          <p className="text-xs text-slate-500 mt-2" data-testid="draws-last-refresh">
            Last {drawStatus.trigger === "auto" ? "auto-refresh" : "refresh"}: {new Date(drawStatus.last_run).toLocaleString()}
            {drawStatus.result ? ` · ${drawStatus.result.with_draws} draws posted` : ""}
          </p>
        )}
      </div>

      {/* Official CPRA results → stock earnings */}
      <div className="rounded-xl border-2 border-[#3B6B1E]/30 bg-[#F3F8EF] p-5 mb-6">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><Download className="w-4 h-4 text-[#3B6B1E]" /> Official Results & Stock Earnings</div>
        <p className="text-sm text-slate-600 mb-4">Scrape the official CPRA results and match each rider's payout to the animal they drew, so the Rodeo Stock dashboard shows real money paid out on every horse and bull. Auto-refreshes with the daily draw refresh. Safe to re-run.</p>
        <button onClick={importResults} disabled={resultsLoading} data-testid="import-results-btn" className="flex items-center gap-2 bg-[#3B6B1E] hover:bg-[#2f5518] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
          {resultsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import Official Results
        </button>
        <RefreshLine st={scrapeStatus?.results_refresh} testid="results-last-refresh" />
      </div>

      {/* Season standings → athlete earnings */}
      <div className="rounded-xl border-2 border-[#E68A2E]/40 bg-[#FDF6EC] p-5 mb-6">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><Award className="w-4 h-4 text-[#E68A2E]" /> Season Standings & Athlete Earnings</div>
        <p className="text-sm text-slate-600 mb-4">Pull the official CPRA season standings from rodeocanada.com. This is the <b>authoritative money</b> shown on the Pro Stats leaderboard and every athlete profile. Run this whenever athlete earnings look wrong or out of date. Auto-refreshes with the daily refresh. Safe to re-run.</p>
        <button onClick={importStandings} disabled={standingsLoading} data-testid="import-standings-btn" className="flex items-center gap-2 bg-[#E68A2E] hover:bg-[#cf7a24] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
          {standingsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} Import Standings (Rodeo Canada)
        </button>
        <RefreshLine st={scrapeStatus?.standings_refresh} testid="standings-last-refresh" />
      </div>

      {/* CFR live-results sheet → go-rounds + per-rider breakdown */}
      <div className="rounded-xl border-2 border-[#7C3AED]/30 bg-[#F6F2FD] p-5 mb-6">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#7C3AED]" /> CFR Live Results</div>
        <p className="text-sm text-slate-600 mb-4">Pull the official <b>Canadian Finals Rodeo</b> live-results sheet — every go-round (incl. no-times) and each rider's round-by-round breakdown shown on their My Riders card. Reads whatever rounds the sheet currently publishes. Auto-refreshes nightly and when the CFR tab is opened. Safe to re-run.</p>
        <button onClick={importCfr} disabled={cfrLoading} data-testid="import-cfr-btn" className="flex items-center gap-2 bg-[#7C3AED] hover:bg-[#6b2fd1] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
          {cfrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Refresh CFR Results
        </button>
        <RefreshLine st={scrapeStatus?.cfr_refresh} testid="cfr-last-refresh" />
      </div>

      {/* Rodeo Count Rules — single source of truth */}
      <CountRulesEditor />

      {/* Feature rollout — control which menu items regular users can see */}
      <div className="rounded-xl border-2 border-[#C8000F]/25 bg-[#FDF2F3] p-5 mb-6" data-testid="feature-rollout">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><ToggleLeft className="w-4 h-4 text-[#C8000F]" /> Feature Rollout</div>
        <p className="text-sm text-slate-600 mb-4">Tick the menu items you're ready to release to regular users. Unticked items stay hidden from them — but you (admin) always see everything, marked <span className="font-semibold">Hidden</span>. Applies to the sidebar and mobile menus.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-4">
          {NAV.map((n) => {
            const on = isReleased(n.to);
            return (
              <button key={n.to} onClick={() => toggleFlag(n.to)} data-testid={`flag-${n.to}`}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${on ? "bg-white border-[#C8000F]/40" : "bg-slate-50 border-slate-200"}`}>
                <span className={`w-5 h-5 rounded flex items-center justify-center border shrink-0 ${on ? "bg-[#C8000F] border-[#C8000F]" : "border-slate-300"}`}>
                  {on && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                </span>
                <span className="flex-1 text-sm font-semibold text-slate-800">{n.label}</span>
                {on ? <Eye className="w-4 h-4 text-[#3B8A2B]" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
              </button>
            );
          })}
        </div>
        <button onClick={saveFlags} disabled={savingFlags} data-testid="save-flags-btn"
          className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
          {savingFlags ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save rollout
        </button>
      </div>

      {/* CCA / PRCA import — persist rodeos + results as full rodeos */}
      <div className="rounded-xl border-2 border-[#003087]/25 bg-[#F1F5FB] p-5 mb-6">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><Download className="w-4 h-4 text-[#003087]" /> Bring in CCA &amp; PRCA</div>
        <p className="text-sm text-slate-600 mb-4">Pull rodeos (schedule) and results into the app for the other associations, so they appear everywhere and power Standings. <b>CCA</b> imports rodeos + per-rider payout results. <b>PRCA</b> imports the full rodeo schedule (draws come from each user's connected PRCA login).</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => importAssoc("cca")} disabled={assocStatus?.cca?.running} data-testid="import-cca-btn"
            className="flex items-center gap-2 bg-[#8C6239] hover:bg-[#734f2d] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
            {assocStatus?.cca?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import CCA (rodeos + results)
          </button>
          <button onClick={() => importAssoc("prca")} disabled={assocStatus?.prca?.running} data-testid="import-prca-btn"
            className="flex items-center gap-2 bg-[#003087] hover:bg-[#00246a] text-white rounded-lg px-5 py-3 text-sm font-bold disabled:opacity-60">
            {assocStatus?.prca?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import PRCA (rodeos)
          </button>
        </div>
        <div className="mt-3 space-y-1 text-xs text-slate-500" data-testid="assoc-import-status">
          {["cca", "prca"].map((k) => {
            const s = assocStatus?.[k];
            if (!s) return null;
            return (
              <div key={k}>
                <b className="uppercase">{k}</b>: {s.running ? `importing… (${s.phase})` : s.error ? `error: ${s.error}` : s.done ? "done" : "idle"}
                {(s.rodeos_created || s.rodeos_updated) ? ` · ${s.rodeos_created} new, ${s.rodeos_updated} updated rodeos` : ""}
                {s.result_rows ? ` · ${s.result_rows} result rows` : ""}
              </div>
            );
          })}
        </div>
        <RefreshLine st={scrapeStatus?.cca_refresh} testid="cca-last-refresh" label="CCA" />
        <RefreshLine st={scrapeStatus?.prca_refresh} testid="prca-last-refresh" label="PRCA" />
      </div>

      <PrcaDataManager />

      {/* CSV import */}
      <div className="rounded-xl border border-[#E4E7EB] p-5 mb-6">
        <div className="font-display font-bold mb-1">Import Rodeo Schedules</div>
        <p className="text-sm text-slate-500 mb-4">Pull live Alberta rodeos from RodeoFinder, or bulk-load your own CSV.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={scrapeImport} disabled={scraping} data-testid="scrape-import-btn" className="flex items-center gap-2 bg-[#5FA83B] hover:bg-[#4d8c30] text-white rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
            {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />} Import Alberta (RodeoFinder)
          </button>
          <button onClick={downloadTemplate} data-testid="download-template-btn" className="flex items-center gap-2 border border-[#D7DBE0] rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-[#F7F8FA]"><Download className="w-4 h-4" /> CSV Template</button>
          <input ref={fileRef} type="file" accept=".csv" onChange={doImport} className="hidden" data-testid="csv-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="upload-csv-btn" className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <form onSubmit={submit} className="rounded-xl border border-[#E4E7EB] p-6 space-y-4" data-testid="admin-rodeo-form">
          <div className="font-display font-bold">Add Rodeo Manually</div>
          <div><label className={label}>Rodeo name</label><input className={input} required data-testid="admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <label className={label}>Associations</label>
            <div className="flex flex-wrap gap-1.5">
              {SIDEBAR_ASSOCIATIONS.map((a) => (
                <button type="button" key={a} onClick={() => setForm({ ...form, associations: toggle(form.associations, a) })}
                  className={`text-xs px-3 py-1.5 rounded-full ${form.associations.includes(a) ? "bg-[#C8000F] text-white" : "bg-[#F1F3F5] text-slate-600"}`}>{a}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>City</label><input className={input} required data-testid="admin-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><label className={label}>Province</label><input className={input} required data-testid="admin-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>Added ($)</label><input className={input} type="number" data-testid="admin-money" value={form.added_money} onChange={(e) => setForm({ ...form, added_money: e.target.value })} /></div>
            <div><label className={label}>Lat</label><input className={input} type="number" step="0.01" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} /></div>
            <div><label className={label}>Lng</label><input className={input} type="number" step="0.01" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Entry opens</label><input className={input} type="date" required data-testid="admin-eo" value={form.entry_open} onChange={(e) => setForm({ ...form, entry_open: e.target.value })} /></div>
            <div><label className={label}>Entry closes</label><input className={input} type="date" required data-testid="admin-ec" value={form.entry_close} onChange={(e) => setForm({ ...form, entry_close: e.target.value })} /></div>
          </div>
          <div>
            <label className={label}>Events</label>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_CATEGORIES.map((ev) => (
                <button type="button" key={ev} onClick={() => setForm({ ...form, events: toggle(form.events, ev) })}
                  className={`text-[11px] px-2.5 py-1 rounded-full ${form.events.includes(ev) ? "bg-[#C8000F] text-white" : "bg-[#F1F3F5] text-slate-600"}`}>{ev}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={label + " mb-0"}>Performances</label>
              <button type="button" onClick={() => setForm((f) => ({ ...f, performances: [...f.performances, { label: `Perf #${f.performances.length + 1}`, type: "perf", time: "7:00 PM", date: "" }] }))} className="text-xs text-[#C8000F] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            {form.performances.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_120px_28px] gap-2 items-center mb-2">
                <input className={input + " !py-2"} value={p.label} onChange={(e) => setPerf(i, "label", e.target.value)} />
                <select className={input + " !py-2 !px-2"} value={p.type} onChange={(e) => setPerf(i, "type", e.target.value)}><option value="perf">Perf</option><option value="slack">Slack</option></select>
                <input className={input + " !py-2 !px-2"} type="date" value={p.date} onChange={(e) => setPerf(i, "date", e.target.value)} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, performances: f.performances.filter((_, idx) => idx !== i) }))} className="text-slate-400 hover:text-red-500 flex justify-center"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div><label className={label}>Ground rules</label><textarea className={input} rows={2} value={form.ground_rules} onChange={(e) => setForm({ ...form, ground_rules: e.target.value })} /></div>
          <button type="submit" disabled={saving} data-testid="admin-submit-btn" className="w-full bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add Rodeo
          </button>
        </form>

        <div className="rounded-xl border border-[#E4E7EB] p-5 h-fit">
          <div className="font-display font-bold mb-4">All Rodeos ({rodeos.length})</div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {rodeos.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-[#EEF0F2]" data-testid={`admin-rodeo-${r.id}`}>
                <div className="min-w-0"><div className="text-sm font-medium truncate">{r.name}</div><div className="text-xs text-slate-500">{r.city}, {r.state} · {money(r.added_money)}</div></div>
                <button onClick={() => remove(r.id)} data-testid={`admin-delete-${r.id}`} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
